import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { useAuth } from '../../../../contexts/AuthContext';
import { EmailEditor } from './EmailEditor';
import { TEMPLATES, templateByKey } from './emailTemplate';

/**
 * Settings → Templates from mockup v0.5: the campaign templates (EN/DE) and the automated GDPR
 * consent renewal (EN/DE). Edits are not saved yet; "Export email HTML" works and shows exactly
 * what will be sent (design v0.6 decision 19).
 */
export function TemplatesTab() {
  const { user } = useAuth();
  const [key, setKey] = useState(TEMPLATES[0].key);
  const [subjects, setSubjects] = useState<Record<string, string>>({});
  const meta = templateByKey(key);
  const subject = subjects[key] ?? meta.subject;

  const Item = ({ k }: { k: string }) => (
    <button
      type="button"
      onClick={() => setKey(k)}
      aria-current={key === k ? 'true' : undefined}
      className={`block w-full rounded px-3 py-2 text-left text-sm ${key === k ? 'bg-zinc-900 text-white' : 'text-zinc-700 hover:bg-zinc-100'}`}
    >
      {templateByKey(k).name}
    </button>
  );

  return (
    <div className="grid gap-5 lg:grid-cols-[220px_1fr]">
      <nav aria-label="Templates" className="space-y-1">
        <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">Campaign templates</p>
        {TEMPLATES.filter((t) => !t.automated).map((t) => (
          <Item key={t.key} k={t.key} />
        ))}
        <p className="px-3 pb-1 pt-4 text-xs font-semibold uppercase tracking-wide text-zinc-500">Automated · GDPR</p>
        {TEMPLATES.filter((t) => t.automated).map((t) => (
          <Item key={t.key} k={t.key} />
        ))}
      </nav>

      <section className="space-y-3 rounded-lg border border-solid border-pearl bg-white p-5">
        <h2 className="text-base font-semibold text-zinc-900">{meta.name}</h2>
        <div className="text-sm">
          <label htmlFor="tmpl-subject" className="mb-1 block text-zinc-600">
            Subject
          </label>
          <input
            id="tmpl-subject"
            value={subject}
            onChange={(e) => setSubjects((s) => ({ ...s, [key]: e.target.value }))}
            className="w-full rounded border border-solid border-zinc-300 px-3 py-2 text-sm"
          />
        </div>
        <EmailEditor key={key} meta={meta} subject={subject} signerName={meta.automated ? 'Talent Acquisition' : user?.displayName || ''} />

        {meta.automated && (
          <p className="rounded bg-zinc-50 p-3 text-sm text-zinc-700">
            <b>How renewal works.</b> Sent automatically from the TA sender at month 22, with a reminder at month 23 and a final
            notice 7 days before consent ends. Both buttons and the profile link open the candidate’s portal through a
            personal, expiring link; the click itself changes nothing, the choice is made in the portal. Confirm → consent
            renews for 24 months. Opt out → data deleted within 24 hours. No answer → deleted on the expiry date.
          </p>
        )}

        <div className="flex items-center gap-3 border-t border-solid border-pearl pt-3">
          <span className="text-xs text-zinc-500">Templates are not saved yet: changes are lost when you switch or leave.</span>
          <span className="ml-auto" />
          <Button size="sm" disabled>
            Save changes
          </Button>
        </div>
      </section>
    </div>
  );
}
